import React from "react"
import { Action } from "@kit/design/modal"
import Stack from "@kit/design/stack"
import Icon from "@kit/design/icon"
import Text from "@kit/design/text"
import Note from "@kit/design/note"
import analytics from "utils/analytics"
import usePromoCode from "hooks/use-promo-code"

const Discount = ({ promoCode }) => {
  const { promoCode: contextPromoCode, removePromoCode } = usePromoCode()

  if (!contextPromoCode || !promoCode) {
    return null
  }
  if (promoCode.valid) {
    return (
      <Note
        type="success"
        title="Discount applied!"
        message={promoCode.message}
        className="mb-8"
      />
    )
  } else {
    return (
      <Note
        type="error"
        title={`${promoCode.redemption_code} cannot be applied`}
        message={promoCode.message}
        className="mb-8"
        action={{
          label: "Dismiss",
          onClick: removePromoCode
        }}
      />
    )
  }
}

export function PlanSwitchConfirmation({
  fromPlan,
  toPlan,
  onCancel = () => {},
  onConfirm = () => {},
  disableButton,
  message
}) {
  const cancelPlanSwitch = () => {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "cancel_plan_switch",
      text: "Cancel",
      location: "plan_switch_confirmation_modal",
      from_plan: fromPlan.display_name,
      to_plan: toPlan.display_name
    })
    onCancel()
  }

  const confirmPlanSwitch = () => {
    analytics.track("Element", "Clicked", {
      element_type: "button",
      name: "confirm_plan_switch",
      text: "Yes, switch my plan",
      location: "plan_switch_confirmation_modal",
      from_plan: fromPlan.display_name,
      to_plan: toPlan.display_name
    })

    onConfirm(fromPlan, toPlan)
  }
  return (
    <>
      <Discount promoCode={toPlan.promo_code} />
      <div className="flex items-center space-x-2">
        <Stack spacing={4} className="w-1/2">
          <h3 className="text-base font-semibold">Your current plan</h3>

          <div className="bg-gray-50 rounded px-4 py-6">
            <h4 className="text-lg font-bold">{fromPlan.display_name} Plan</h4>
            <Text size="xs" weight="light" className="leading-8">
              Up to {fromPlan.subscriber_limit} subscribers
            </Text>
            <Text className="font-heading text-3xl mt-2">{fromPlan.text}</Text>
            <Text size="xs" weight="light" className="mt-2">
              per {fromPlan.interval}
            </Text>
          </div>
        </Stack>

        <Icon className="mt-6" name="arrow-right" />

        <Stack spacing={4} className="w-1/2">
          <h3 className="text-base font-semibold">Your new plan</h3>

          <div className="bg-green-50 rounded px-4 py-6">
            <h4 className="text-lg font-bold">{toPlan.display_name} Plan</h4>
            <Text size="xs" weight="light" className="leading-8">
              Up to {toPlan.subscriber_limit} subscribers
            </Text>
            {toPlan.promo_code?.valid ? (
              <Text className="font-heading text-3xl mt-2">
                <span className="line-through">{toPlan.text}</span>
                <span className="ml-2">{toPlan.promo_code?.text}</span>
              </Text>
            ) : (
              <Text className="font-heading text-3xl mt-2">{toPlan.text}</Text>
            )}
            <Text size="xs" weight="light" className="mt-2">
              per {toPlan.interval}
            </Text>
          </div>
        </Stack>
      </div>

      <Text size="sm" className="mt-6">
        {message ||
          "Any credit you have remaining will be applied to your account."}
      </Text>

      <Action is="secondary" onClick={cancelPlanSwitch}>
        Cancel
      </Action>

      <Action
        is="primary"
        onClick={confirmPlanSwitch}
        disabled={disableButton}
        data-testid="confirm-plan-switch-button"
      >
        Yes, change my plan
      </Action>
    </>
  )
}
